import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "../../axios";
import apiError from "../../error";
import aivishingImg from "../../assets/create_campaign/aivishing.png";
import vishingImg from "../../assets/create_campaign/vishing.png";
import whatsappImgIVR from "../../assets/create_campaign/whatsapp-ivr.png";
import whatsappImgChatbox from "../../assets/create_campaign/chatbox.png";
import "./ai-simulation-attack.css";

// Maps the local radio mode to the backend simulation-type name.
const SIMULATION_TYPE_BY_MODE = {
  simulation: "Simulation Attack",
  "simulation+training": "Simulation Attack + Training",
};

// Normalization helpers mirror campaign-draft-preview.js so the attack-type
// values/labels stay consistent across the AI flow.
function normalizeSimulationType(value) {
  const raw = String(value || "").trim();
  if (raw === "Simulation") return "Simulation Attack";
  return raw || "Simulation Attack";
}

function normalizeAttackType(value) {
  const raw = String(value || "").trim();
  if (raw === "Email Phishing") return "Phishing";
  if (raw === "QR Code") return "QRcode";
  return raw || "Phishing";
}

function formatAttackTypeLabel(value) {
  const attackType = normalizeAttackType(value);
  return attackType === "QRcode" ? "QR Code" : attackType;
}

function coerceBoolean(value, fallback = false) {
  if (value === true || value === false) return value;
  if (value === 1 || value === 0) return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes", "on"].includes(normalized)) return true;
    if (["false", "0", "no", "off", ""].includes(normalized)) return false;
  }
  return fallback;
}

function normalizeAttackTypeOption(value) {
  const attackType = value?.attack_type || value;
  const rawName = String(attackType?.name || attackType?.value || "").trim();
  if (!rawName) return null;
  const name = normalizeAttackType(rawName);
  return {
    id: attackType?.id ?? value?.attackTypeId ?? null,
    value: name,
    label: attackType?.label || formatAttackTypeLabel(name),
    type: attackType?.type || "",
  };
}

function dedupeAttackTypeOptions(options = []) {
  const seen = new Set();
  return options.filter((option) => {
    if (!option?.value || seen.has(option.value)) return false;
    seen.add(option.value);
    return true;
  });
}

// Vishing/WhatsApp inbound (AI conversational) flows are keyed under a
// `-inbound` suffix in the backend config, so resolve the effective key
// before looking up the rules. Mirrors campaign-draft-preview's
// getEffectiveAttackTypeKey.
function getEffectiveAttackTypeKey(attackType, targetInteractionType, isAIbased) {
  const name = normalizeAttackType(attackType);
  const interaction = String(targetInteractionType || "").trim().toLowerCase();

  if (name === "Vishing" && interaction === "inbound") {
    return "Vishing-inbound";
  }
  if (name === "WhatsApp" && interaction === "inbound" && coerceBoolean(isAIbased, false)) {
    return "WhatsApp-inbound";
  }
  return name;
}

// Looks up the backend rules for the selected attack type, mirroring
// campaign-draft-preview's getAttackTypeRules.
function getAttackTypeRules(attackType, attackTypeConditions = {}, targetInteractionType, isAIbased) {
  const effectiveKey = getEffectiveAttackTypeKey(attackType, targetInteractionType, isAIbased);
  return attackTypeConditions[effectiveKey] || attackTypeConditions[normalizeAttackType(attackType)] || {};
}

// Vishing/WhatsApp are also judged via targetInteractionType + isAIbased,
// so they need an interaction-mode selector. Default interaction state when
// an attack type is (de)selected matches campaign-draft-preview's
// applyAttackVectorDefaults.
function deriveInteractionDefaults(attackType) {
  const name = normalizeAttackType(attackType);
  if (name === "Vishing" || name === "WhatsApp") {
    return { targetInteractionType: "outbound", isAIbased: false };
  }
  return { targetInteractionType: "", isAIbased: false };
}

function normalizeSimulationTypeOption(simulationType) {
  const rawName = String(simulationType?.name || simulationType?.value || "").trim();
  if (!rawName) return null;
  const name = normalizeSimulationType(rawName);
  const simulationAttacks = Array.isArray(simulationType?.simulation_attacks)
    ? simulationType.simulation_attacks
    : [];

  return {
    id: simulationType?.id ?? null,
    value: name,
    label: simulationType?.label || name,
    attackTypeOptions: dedupeAttackTypeOptions(
      simulationAttacks.map(normalizeAttackTypeOption).filter(Boolean)
    ),
  };
}

let sacInstanceCounter = 0;

const AiSimulationAttack = ({ onDone, initialMode }) => {
  // Native <input type="radio"> groups by `name` across the whole document, not per
  // React component instance. The chat keeps every past AI message mounted as the
  // user scrolls, so if this question is ever shown more than once, a shared static
  // name (e.g. "sac-mode") makes the browser treat all of those instances' radios as
  // ONE mutually-exclusive group — checking "Simulation + Training" in a later
  // message silently unchecks the earlier message's radio natively (no onChange
  // fires, so React never re-asserts it), leaving it visually stuck on "Simulation"
  // even though nothing about that earlier answer actually changed.
  const [instanceName] = useState(() => `sac-mode-${++sacInstanceCounter}`);

  const [mode, setMode]                       = useState(initialMode === "simulation+training" ? "simulation+training" : "simulation");
  const [selected, setSelected]               = useState(null);
  const [credentialHarvesting, setCredential] = useState(false);

  // Interaction mode for Vishing/WhatsApp, judged via these two fields.
  const [targetInteractionType, setTargetInteractionType] = useState("");
  const [isAIbased, setIsAIbased]                         = useState(false);

  const [simulationTypeOptions, setSimulationTypeOptions] = useState([]);
  const [attackTypeConditions, setAttackTypeConditions]   = useState({});
  const [loading, setLoading]                             = useState(false);

  const fetchAttackTypeConditions = useCallback(async () => {
    try {
      const response = await axios.get("/api/config/attack-type-conditions");
      const conditions = response.data?.attackTypeConditions;
      setAttackTypeConditions(
        conditions && typeof conditions === "object" && !Array.isArray(conditions)
          ? conditions
          : {}
      );
    } catch (err) {
      apiError(err);
      setAttackTypeConditions({});
    }
  }, []);

  const fetchSimulationTypeOptions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get("/api/simulation/type", {});
      const options = Array.isArray(response.data?.simulation_type)
        ? response.data.simulation_type.map(normalizeSimulationTypeOption).filter(Boolean)
        : [];
      setSimulationTypeOptions(options);
    } catch (err) {
      apiError(err);
      setSimulationTypeOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAttackTypeConditions();
  }, [fetchAttackTypeConditions]);

  useEffect(() => {
    fetchSimulationTypeOptions();
  }, [fetchSimulationTypeOptions]);

  // Attack types available for the currently selected mode, sourced from
  // the matching backend simulation type.
  const visibleTypes = useMemo(() => {
    const simulationType = SIMULATION_TYPE_BY_MODE[mode];
    const option = simulationTypeOptions.find((item) => item.value === simulationType);
    return option?.attackTypeOptions || [];
  }, [mode, simulationTypeOptions]);

  const selectedOption = useMemo(
    () => visibleTypes.find((t) => t.value === selected) || null,
    [visibleTypes, selected]
  );

  // Credential harvesting support comes from the backend attack-type
  // conditions, keyed by the selected attack type.
  const credentialSupported = useMemo(
    () => coerceBoolean(
      getAttackTypeRules(selected, attackTypeConditions, targetInteractionType, isAIbased).isCredentialHarvestingValid,
      false
    ),
    [selected, attackTypeConditions, targetInteractionType, isAIbased]
  );

  const handleSelect = (key) => {
    const nextSelected = key === selected ? null : key;
    setSelected(nextSelected);

    // Reset the interaction mode to the new attack type's defaults.
    const defaults = deriveInteractionDefaults(nextSelected);
    setTargetInteractionType(defaults.targetInteractionType);
    setIsAIbased(defaults.isAIbased);

    // Reset credential harvesting if the new type (with its default
    // interaction mode) does not support it.
    const nextSupported = coerceBoolean(
      getAttackTypeRules(
        nextSelected,
        attackTypeConditions,
        defaults.targetInteractionType,
        defaults.isAIbased
      ).isCredentialHarvestingValid,
      false
    );
    if (!nextSupported) {
      setCredential(false);
    }
  };

  const handleModeChange = (nextMode) => {
    setMode(nextMode);
    setSelected(null);
    setCredential(false);
    setTargetInteractionType("");
    setIsAIbased(false);
  };

  // Toggle between IVR (outbound, non-AI) and AI Conversational (inbound, AI)
  // for Vishing/WhatsApp, mirroring CampaignName's handleAIbased.
  const handleInteractionMode = (aiBased) => {
    setIsAIbased(aiBased);
    setTargetInteractionType(aiBased ? "inbound" : "outbound");
  };

  const handleDone = () => {
    if (!onDone) return;

    const typeLabel = selectedOption?.label || selected;

    onDone({
      mode,
      type: selected,
      typeLabel,
      credentialHarvesting: credentialSupported ? credentialHarvesting : false,
      targetInteractionType,
      isAIbased,
    });
  };

  return (
    <div className="sac-wrapper">

      {/* Title */}
      <p className="sac-title">
        Great — what type of campaign would you like to run?
      </p>

      {/* Radio buttons */}
      <div className="sac-radio-group">
        <label className={`sac-radio-label${mode === "simulation" ? " active" : ""}`}>
          <input
            type="radio"
            name={instanceName}
            value="simulation"
            checked={mode === "simulation"}
            onChange={() => handleModeChange("simulation")}
          />
          Simulation
        </label>
        <label className={`sac-radio-label${mode === "simulation+training" ? " active" : ""}`}>
          <input
            type="radio"
            name={instanceName}
            value="simulation+training"
            checked={mode === "simulation+training"}
            onChange={() => handleModeChange("simulation+training")}
          />
          Simulation + Training
        </label>
      </div>

      {/* Attack type badges */}
      {loading && visibleTypes.length === 0 ? (
        <p className="sac-loading">Loading attack types…</p>
      ) : (
        <div className="sac-badge-group">
          {visibleTypes.map(({ id, value, label }) => (
            <button
              key={id ?? value}
              type="button"
              className={`sac-badge${selected === value ? " selected" : ""}`}
              onClick={() => handleSelect(value)}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Vishing interaction-mode selector (IVR vs AI Conversational) */}
      {selected === "Vishing" && (
        <div className="vishing-options-wrapper ec-vishing-wrapper">
          <div className="row">
            <div className="col-md-6 mb-3">
              <div
                className={`vishing-card${!isAIbased ? " active" : ""}`}
                onClick={() => handleInteractionMode(false)}
              >
                <div className="vishing-img-wrapper">
                  <img src={vishingImg} alt="IVR" />
                </div>
                <div className="vishing-content">
                  <div className="vishing-title">IVR (Keypad-based)</div>
                  <div className="vishing-desc">Automated pre-recorded calls that attempt to extract information using keypad inputs</div>
                </div>
                <div className="vishing-radio">
                  <input type="radio" checked={!isAIbased} readOnly />
                </div>
              </div>
            </div>
            <div className="col-md-6 mb-3">
              <div
                className={`vishing-card${isAIbased ? " active" : ""}`}
                onClick={() => handleInteractionMode(true)}
              >
                <div className="vishing-img-wrapper">
                  <img src={aivishingImg} alt="AI" />
                </div>
                <div className="vishing-content">
                  <div className="vishing-title">AI Conversational (Voice Bot)</div>
                  <div className="vishing-desc">Human-like AI voice interaction that dynamically engages users to extract sensitive information.</div>
                </div>
                <div className="vishing-radio">
                  <input type="radio" checked={isAIbased} readOnly />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp interaction-mode selector (Direct vs AI Conversational) */}
      {selected === "WhatsApp" && (
        <div className="vishing-options-wrapper ec-vishing-wrapper">
          <h5 className="ec-vishing-section-title">WhatsApp Phishing Mode</h5>
          <div className="row">
            <div className="col-md-6 mb-3">
              <div
                className={`vishing-card${!isAIbased ? " active" : ""}`}
                onClick={() => handleInteractionMode(false)}
              >
                <div className="vishing-img-wrapper">
                  <img src={whatsappImgIVR} alt="WhatsApp" />
                </div>
                <div className="vishing-content">
                  <div className="vishing-title">Direct WhatsApp Message</div>
                  <div className="vishing-desc">Send tracked WhatsApp messages from the configured messaging gateway.</div>
                </div>
                <div className="vishing-radio">
                  <input type="radio" checked={!isAIbased} readOnly />
                </div>
              </div>
            </div>
            <div className="col-md-6 mb-3">
              <div
                className={`vishing-card${isAIbased ? " active" : ""}`}
                onClick={() => handleInteractionMode(true)}
              >
                <div className="vishing-img-wrapper">
                  <img src={whatsappImgChatbox} alt="AI WhatsApp" />
                </div>
                <div className="vishing-content">
                  <div className="vishing-title">AI Conversational (Chat Bot)</div>
                  <div className="vishing-desc">Send an email with a tracked WhatsApp link, then continue the interaction in AI chat.</div>
                </div>
                <div className="vishing-radio">
                  <input type="radio" checked={isAIbased} readOnly />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Credential harvesting selection */}
      {selected && (
        <div className="sac-credential">
          <p className="sac-credential-label">Credential Harvesting</p>

          {credentialSupported ? (
            <div className="sac-badge-group">
              <button
                type="button"
                className={`sac-badge${credentialHarvesting ? " selected" : ""}`}
                onClick={() => setCredential(true)}
              >
                Yes
              </button>
              <button
                type="button"
                className={`sac-badge${!credentialHarvesting ? " selected" : ""}`}
                onClick={() => setCredential(false)}
              >
                No
              </button>
            </div>
          ) : (
            <p className="sac-credential-na">
              Credential harvesting is not available for the selected attack type.
            </p>
          )}
        </div>
      )}

      {/* Done button */}
      {selected && (
        <div className="sac-footer">
          <button
            type="button"
            className="sac-done-btn"
            onClick={handleDone}
          >
            ✔ Done
          </button>
        </div>
      )}

    </div>
  );
};

export default AiSimulationAttack;
